import { getApiErrorCode, getApiErrorMessage } from '@shared/utils/errorHandling';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { InvitationService } from '../services/invitation.service';
import { CreateInvitationRequest, Invitation } from '../types/invitation';

interface UseInvitationsProps {
  organizationId: string;
  /**
   * Called when an invitation cannot be deleted because the member already
   * published content: their work has to be transferred to somebody else first.
   */
  onDeleteBlockedByContent?: (invitationId: string) => void;
}

export const useInvitations = ({
  organizationId,
  onDeleteBlockedByContent,
}: UseInvitationsProps) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await InvitationService.getOrganizationInvitations(organizationId);
      setInvitations(data);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to load invitations', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const createInvitation = async (data: CreateInvitationRequest) => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      await InvitationService.createInvitation(organizationId, data);
      // Nothing reaches the invitee yet: GCCA reviews it first.
      toast.success(`Invitation for ${data.email} sent to GCCA for approval`);
      await fetchInvitations(); // Refresh the list
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to send invitation', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteInvitation = async (invitationId: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await InvitationService.deleteInvitation(invitationId);
      toast.success(
        result?.purgedUser
          ? 'Invitation and the unfinished account were deleted. This email can be invited again.'
          : 'Invitation deleted successfully'
      );
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
    } catch (err) {
      // The member already published something, so deleting them outright would
      // orphan their content: hand the caller over to the transfer flow.
      if (getApiErrorCode(err) === 'USER_HAS_CONTENT' && onDeleteBlockedByContent) {
        onDeleteBlockedByContent(invitationId);
        return;
      }

      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to delete invitation', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resendInvitation = async (invitationId: string) => {
    setLoading(true);
    setError(null);

    try {
      const updatedInvitation = await InvitationService.resendInvitation(invitationId);
      toast.success('Invitation resent successfully');
      console.log('Invitation resent successfully', updatedInvitation);

      // Update the invitation in the list
      setInvitations(invitations.map(inv =>
        inv.id === invitationId ? updatedInvitation : inv
      ));
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to resend invitation', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationLink = (invitationId: string) => {
    const invitationLink = `${window.location.origin}/invitation?id=${invitationId}`;
    navigator.clipboard.writeText(invitationLink)
      .then(() => {
        toast.success('Invitation link copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy invitation link', err);
        toast.error('Failed to copy invitation link');
      });
  };

  useEffect(() => {
    if (organizationId) {
      fetchInvitations();
    }
  }, [organizationId]);

  return {
    invitations,
    loading,
    error,
    fetchInvitations,
    createInvitation,
    deleteInvitation,
    resendInvitation,
    copyInvitationLink,
  };
};
